/* @flow */
import React, { type Node, useContext, useEffect, useState } from 'react';
import Slider from 'react-slick';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { TCity } from '../../types/city';
import type { THelpful } from '../../types/helpful';

import Breadcrumbs from '../../components/Breadcrumbs';
import Slick from '../../components/Layout/slick';

import {
	ButtonWrapper,
	ExpertiseArticles,
	Header,
	Root,
	Section,
	Subtitle,
	SubtitleLink,
	Title,
	VideoSlider,
} from './styles';
import { Container } from '../../components/Layout/styles';
import { AppContext } from '../../components/AppContext';
import { fetchContentData } from '../../actions/helpful';
import type { TDispatch } from '../../types/core';
import Expertise from './HelpfulExpertise';
import Video from './HelpfulVideo';
import Knowledge from './HelpfulKnowledge';
// import Article from './HelpfulArticle';
import News from './HelpfulNews';
import Button from '../../components/Button2.0';

const NextArrow = ({ onClick }) => <button className="slick-arrow slick-next" onClick={onClick} type="button" />;

const PrevArrow = ({ onClick }) => <button className="slick-arrow slick-prev" onClick={onClick} type="button" />;

const SLIDER = {
	dots: false,
	arrows: true,
	centerMode: true,
	speed: 500,
	slidesToShow: 3,
	swipeToSlide: true,
	nextArrow: <NextArrow />,
	prevArrow: <PrevArrow />,
	responsive: [
		{
			breakpoint: 769,
			settings: {
				slidesToShow: 2,
				swipeToSlide: true,
			},
		},
		{
			breakpoint: 561,
			settings: {
				arrows: false,
				slidesToShow: 1,
				swipeToSlide: true,
			},
		},
	],
};

const Helpful = (): Node => {
	const dispatch: TDispatch = useDispatch();
	const store = useStore();
	const context = useContext(AppContext);

	const city: TCity = useSelector((state) => state.city);
	const content: THelpful = useSelector((state) => state.helpful.contentData);
	const { expertiseList, videoList, knowledgeList, newsList /* , articleList */ } = content || {};

	const [sliderPaddings, setSliderPaddings] = useState(0);

	const breadcrumbs = [
		{
			index: 1,
			title: 'Главная',
			link: '/',
		},
		{
			index: 2,
			title: 'Статьи',
			link: `/${city.link}/articles/`,
		},
	];

	const getScreenWidth = () => {
		if (typeof window !== 'undefined' && window.innerWidth >= 1260) {
			setSliderPaddings((window.innerWidth - 1260) / 2);
		} else {
			setSliderPaddings(50);
		}
	};

	useEffect(() => {
		fetchContentData()(dispatch, store.getState, context);
	}, [city]);

	useEffect(() => {
		getScreenWidth();
		window.addEventListener('resize', getScreenWidth);

		return () => window.removeEventListener('resize', getScreenWidth);
	}, []);

	return (
		<Root>
			<Container>
				<Breadcrumbs data={breadcrumbs} />
				<Header>
					<Title>Статьи</Title>
				</Header>
			</Container>
			{expertiseList?.length > 0 && (
				<Section scheme="light">
					<Container>
						<Subtitle>
							<SubtitleLink to={`/${city.link}/expert/`}>Экспертиза</SubtitleLink>
						</Subtitle>
						<ExpertiseArticles>
							{expertiseList.map((item, idx) => (
								<Expertise city={city} item={item} key={idx.toString()} />
							))}
						</ExpertiseArticles>
						<ButtonWrapper>
							<Button
								isLink
								color="white"
								size="s"
								to={`/${city.link}/expert/`}
								style={{ padding: '0 30px' }}
							>
								Смотреть все материалы
							</Button>
						</ButtonWrapper>
					</Container>
				</Section>
			)}
			{videoList?.length > 0 && (
				<Section scheme="dark">
					<Container>
						<Subtitle>
							<SubtitleLink to={`/video/${city.link}/`}>Видео</SubtitleLink>
						</Subtitle>
					</Container>
					<VideoSlider />
					<Slick />
					<Slider {...SLIDER} centerPadding={`${sliderPaddings}px`}>
						{videoList.map((item, idx) => (
							<Video item={item} key={idx.toString()} />
						))}
					</Slider>
					<ButtonWrapper>
						<Button
							isLink
							color="white"
							bordered
							size="s"
							to={`/video/${city.link}/`}
							style={{ padding: '0 30px' }}
						>
							Все видео
						</Button>
					</ButtonWrapper>
				</Section>
			)}
			{knowledgeList?.length > 0 && (
				<Section scheme="light">
					<Container>
						<Subtitle>
							<SubtitleLink to="/znanie/">Знание</SubtitleLink>
						</Subtitle>
						<ExpertiseArticles>
							{knowledgeList.map((item, idx) => (
								<Knowledge item={item} key={idx.toString()} />
							))}
						</ExpertiseArticles>
						<ButtonWrapper>
							<Button isLink color="white" size="s" to="/znanie/" style={{ padding: '0 30px' }}>
								Перейти в раздел
							</Button>
						</ButtonWrapper>
					</Container>
				</Section>
			)}
			{/* articleList?.length > 0 && (
				<Section>
					<Container>
						<Subtitle>
							<SubtitleLink to="/journal/">Журнал</SubtitleLink>
						</Subtitle>
						<ExpertiseArticles>
							{articleList.map((item, idx) => (
								<Article item={item} key={idx.toString()} />
							))}
						</ExpertiseArticles>
						<ButtonWrapper>
							<Button isLink color="gray" size="s" to="/journal/" style={{ padding: '0 30px' }}>
								Смотреть все материалы
							</Button>
						</ButtonWrapper>
					</Container>
				</Section>
			) */}
			{newsList?.length > 0 && (
				<Section scheme="light">
					<Container>
						<Subtitle>
							<SubtitleLink to="/news/">Новости</SubtitleLink>
						</Subtitle>
						<ExpertiseArticles>
							{newsList.map((item, idx) => (
								<News item={item} key={idx.toString()} />
							))}
						</ExpertiseArticles>
						<ButtonWrapper>
							<Button isLink color="white" size="s" to="/news/" style={{ padding: '0 30px' }}>
								Перейти в раздел
							</Button>
						</ButtonWrapper>
					</Container>
				</Section>
			)}
		</Root>
	);
};

export default Helpful;
